import { useEffect, useState } from 'react';
import { Sparkles, Check, ChevronRight, MapPin, Phone, Mail } from 'lucide-react';
import { LANDINGS, LANDINGS_EN, equivalente, conPrecios, formatPrecio, normalizarPrecios, PRECIOS_FALLBACK, type Landing, type Precios } from '../seo/landings';
import { track } from '../lib/track';

// Landing SEO. Se renderiza igual en el navegador y en el build
// (scripts/build-seo-pages.mjs → renderToStaticMarkup), así que nada del
// render puede tocar window/document: eso va en useEffect o en el init.
// Imports relativos para que esbuild lo empaquete sin el alias '@/'.

const GOLD = '#D4AF37';
// Dorado oscuro para TEXTO: el #D4AF37 sobre crema no llega al contraste mínimo (1,98:1).
const GOLD_TEXT = '#866A12';
const NAVY = '#1E3A5F';

// Textos de interfaz de la página (el contenido vive en los JSON por idioma).
const UI = {
  es: {
    inicio: 'Inicio', desde: 'Desde', comunion: 'Comunión', bodas: 'Bodas', navidad: 'Navidad y Reyes',
    ejemplos: 'Ejemplos de diseños',
    ejemplosNota: 'Diseños generados con nuestra IA a partir de fotos. La figura final se modela, se imprime en resina y se pinta a mano.',
    faq: 'Preguntas frecuentes', migas: 'Migas de pan', secciones: 'Secciones', relacionado: 'También te puede interesar',
    ctaTitulo: '¿Lo vemos?', ctaTexto: 'Configúrala en unos minutos y mira el resultado con la IA antes de decidir.',
    ventajas: ['Vista previa con IA, sin compromiso', 'Pintada a mano en Ciudad Real', 'Envío a toda España', 'Caja de regalo incluida'],
    legal: [['/privacidad', 'Privacidad'], ['/terminos', 'Términos'], ['/cookies', 'Cookies'], ['/accesibilidad', 'Accesibilidad']] as [string, string][],
    home: '/',
  },
  en: {
    inicio: 'Home', desde: 'From', comunion: 'Communion', bodas: 'Weddings', navidad: 'Christmas',
    ejemplos: 'Example designs',
    ejemplosNota: 'Designs generated with our AI from photos. The final piece is modelled, 3D printed in resin and hand-painted.',
    faq: 'Frequently asked questions', migas: 'Breadcrumb', secciones: 'Sections', relacionado: 'You may also like',
    ctaTitulo: 'Shall we?', ctaTexto: 'Set it up in a few minutes and see the AI preview before deciding.',
    ventajas: ['Free AI preview, no commitment', 'Hand-painted in our Spanish workshop', 'Worldwide shipping', 'Gift box included'],
    legal: [['/privacidad', 'Privacy'], ['/terminos', 'Terms'], ['/cookies', 'Cookies'], ['/accesibilidad', 'Accessibility']] as [string, string][],
    home: '/en/',
  },
};

// Precios con los que se generó el HTML estático (data-precios en #root):
// arrancar con ellos evita un salto de contenido antes del fetch en vivo.
function preciosIniciales(): Precios {
  if (typeof document === 'undefined') return PRECIOS_FALLBACK;
  try {
    const raw = document.getElementById('root')?.dataset.precios;
    return raw ? normalizarPrecios(JSON.parse(raw)) : PRECIOS_FALLBACK;
  } catch {
    return PRECIOS_FALLBACK;
  }
}

export default function LandingPage({ landing, precios: preciosProp }: { landing: Landing; precios?: Precios }) {
  const [precios, setPrecios] = useState<Precios>(() => preciosProp || preciosIniciales());
  const p = (txt: string) => conPrecios(txt, precios);

  useEffect(() => {
    fetch('/api/site/prices')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setPrecios(normalizarPrecios(d)); })
      .catch(() => {});
    // Misma métrica que la home (activity_log) para ver qué landings traen visitas.
    track('page_view', { landing: landing.slug });
  }, [landing.slug]);

  const t = UI[landing.lang === 'en' ? 'en' : 'es'];
  const catalogo: Landing[] = landing.lang === 'en' ? LANDINGS_EN : LANDINGS;
  const prefijo = landing.lang === 'en' ? '/en/' : '/';
  const related = landing.related.map(s => catalogo.find(l => l.slug === s)).filter((l): l is Landing => !!l);
  // Misma página en el otro idioma, si existe.
  const otroIdioma = equivalente(landing);
  const [img1, img2] = landing.images;
  // En inglés solo existen las páginas de boda; las de comunión siguen en español.
  const grupos: { titulo: string; items: Landing[] }[] = landing.lang === 'en'
    ? [{ titulo: t.bodas, items: LANDINGS_EN }]
    : [
      { titulo: t.comunion, items: LANDINGS.filter(l => l.group === 'comunion') },
      { titulo: t.bodas, items: LANDINGS.filter(l => l.group === 'boda') },
      { titulo: t.navidad, items: LANDINGS.filter(l => l.group === 'navidad') },
    ].filter(g => g.items.length > 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--page-bg, #FAF8F5)' }}>
      <header className="border-b border-[#D4AF37]/20 bg-white/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <a href={t.home} className="flex items-center gap-2" aria-label="Funcomunión">
            <img src="/favicon.png" alt="" width={32} height={32} className="w-8 h-8 object-contain" />
            <span className="text-2xl font-semibold" style={{ color: NAVY, fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Funcomunión</span>
          </a>
          <nav aria-label="Secciones" className="flex items-center gap-4 sm:gap-6 text-sm">
            <a href={t.home} className="text-[#374151] hover:text-[#1E3A5F]">{landing.lang === 'en' ? t.inicio : t.comunion}</a>
            <a href={landing.lang === 'en' ? '/en/wedding-figures' : '/bodas'} className="text-[#374151] hover:text-[#1E3A5F]">{t.bodas}</a>
            {otroIdioma && (
              <a
                href={`${otroIdioma.lang === 'en' ? '/en/' : '/'}${otroIdioma.slug}`}
                hrefLang={otroIdioma.lang}
                className="text-[#374151] hover:text-[#1E3A5F] border border-gray-200 rounded-full px-2 py-0.5"
              >
                {otroIdioma.lang === 'en' ? 'English' : 'Español'}
              </a>
            )}
            {/* En móvil sobra: el CTA principal está justo debajo, en el hero */}
            <a href={landing.cta.href} className="hidden sm:inline-flex btn-nacre px-4 py-2 rounded-full text-sm font-medium">{landing.cta.label}</a>
          </nav>
        </div>
      </header>

      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <nav aria-label={t.migas} className="py-4 text-xs text-[#6B7280]">
          <ol className="flex items-center gap-1 list-none p-0 m-0">
            <li><a href={t.home} className="hover:underline">{t.inicio}</a></li>
            <li aria-hidden="true"><ChevronRight className="w-3 h-3" /></li>
            <li aria-current="page">{landing.nav}</li>
          </ol>
        </nav>

        <section className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center py-6">
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase mb-3" style={{ color: GOLD_TEXT }}>{landing.kicker}</p>
            <h1 className="text-4xl lg:text-5xl font-semibold leading-tight mb-5" style={{ color: NAVY }}>{landing.h1}</h1>
            {landing.intro.map((t, i) => (
              <p key={i} className="text-[#374151] leading-relaxed mb-4">{p(t)}</p>
            ))}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 my-6">
              <span className="text-sm text-[#6B7280]">{t.desde}</span>
              <span className="text-3xl font-bold text-gradient-gold whitespace-nowrap">{formatPrecio(landing.precioDesde ?? precios[landing.price])}</span>
              <span className="text-sm text-[#6B7280]">{p(landing.priceLabel)}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={landing.cta.href} className="btn-nacre inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium">
                <Sparkles className="w-4 h-4" aria-hidden="true" /> {landing.cta.label}
              </a>
              {landing.cta2 && (
                <a
                  href={landing.cta2.href}
                  {...(landing.cta2.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium border border-[#D4AF37]/60 hover:bg-[#D4AF37]/10"
                  style={{ color: NAVY }}
                >
                  {landing.cta2.label}
                </a>
              )}
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-2 text-xs text-[#374151] list-none p-0">
              {t.ventajas.map(v => (
                <li key={v} className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: GOLD }} aria-hidden="true" />{v}</li>
              ))}
            </ul>
          </div>
          {img1 && (
            <figure className="m-0">
              <img
                src={img1.src}
                alt={img1.alt}
                width={img1.w || 800}
                height={img1.h || 800}
                className="w-full h-auto rounded-3xl shadow-elegant-lg bg-white"
                fetchPriority="high"
              />
              {img1.caption && <figcaption className="text-xs text-[#6B7280] mt-2 text-center">{img1.caption}</figcaption>}
            </figure>
          )}
        </section>

        <div className="grid lg:grid-cols-3 gap-10 mt-10">
          <div className="lg:col-span-2 space-y-10">
            {landing.sections.map(s => (
              <section key={s.h2}>
                <h2 className="text-3xl font-semibold mb-4" style={{ color: NAVY }}>{s.h2}</h2>
                {s.p?.map((t, i) => <p key={i} className="text-[#374151] leading-relaxed mb-3">{p(t)}</p>)}
                {s.list && (
                  <ul className="space-y-2 list-none p-0">
                    {s.list.map(t => (
                      <li key={t} className="flex items-start gap-2 text-[#374151] leading-relaxed">
                        <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: GOLD }} aria-hidden="true" />
                        <span>{p(t)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {landing.galeria && landing.galeria.length > 0 && (
              <section>
                <h2 className="text-3xl font-semibold mb-2" style={{ color: NAVY }}>{t.ejemplos}</h2>
                <p className="text-sm text-[#6B7280] mb-4">{landing.galeriaNota || t.ejemplosNota}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {landing.galeria.map(img => (
                    <figure key={img.src} className="m-0">
                      <img
                        src={img.src}
                        alt={img.alt}
                        width={img.w || 800}
                        height={img.h || 800}
                        loading="lazy"
                        className="w-full h-auto rounded-xl bg-white shadow-elegant"
                      />
                      {img.caption && <figcaption className="text-[11px] text-[#6B7280] mt-1">{img.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </section>
            )}

            {landing.faq.length > 0 && (
              <section>
                <h2 className="text-3xl font-semibold mb-4" style={{ color: NAVY }}>{t.faq}</h2>
                <div className="space-y-3">
                  {landing.faq.map(f => (
                    <details key={f.q} className="group rounded-xl border border-gray-200 bg-white p-4">
                      <summary className="cursor-pointer font-medium" style={{ color: NAVY }}>{f.q}</summary>
                      <p className="text-[#374151] leading-relaxed mt-2 mb-0">{p(f.a)}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="space-y-6">
            {img2 && (
              <figure className="m-0">
                <img src={img2.src} alt={img2.alt} width={img2.w || 800} height={img2.h || 800} loading="lazy" className="w-full h-auto rounded-2xl shadow-elegant bg-white" />
                {img2.caption && <figcaption className="text-xs text-[#6B7280] mt-2 text-center">{img2.caption}</figcaption>}
              </figure>
            )}
            <div className="rounded-2xl p-6 text-white" style={{ backgroundColor: NAVY }}>
              <p className="text-xl font-semibold mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>{t.ctaTitulo}</p>
              <p className="text-sm text-white/80 mb-4">{t.ctaTexto}</p>
              <a href={landing.cta.href} className="btn-nacre inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" aria-hidden="true" /> {landing.cta.label}
              </a>
            </div>
            {related.length > 0 && (
              <nav aria-label={t.relacionado} className="rounded-2xl border border-gray-200 bg-white p-6">
                <p className="font-semibold mb-3" style={{ color: NAVY }}>{t.relacionado}</p>
                <ul className="space-y-2 list-none p-0 m-0">
                  {related.map(r => (
                    <li key={r.slug}>
                      <a href={`${prefijo}${r.slug}`} className="text-sm text-[#374151] hover:text-[#1E3A5F] hover:underline inline-flex items-center gap-1">
                        <ChevronRight className="w-3.5 h-3.5" style={{ color: GOLD }} aria-hidden="true" />{r.nav}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </aside>
        </div>
      </main>

      <footer className="text-white" style={{ backgroundColor: 'var(--page-footer, #1E3A5F)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid sm:grid-cols-3 gap-8">
          <div className="text-sm text-white/75 space-y-2">
            <p className="text-xl font-semibold text-white" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>Funcomunión</p>
            <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />Calle Caballeros, 8 · 13001 Ciudad Real</p>
            <p><a href="tel:+34627602815" className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4" aria-hidden="true" />+34 627 602 815</a></p>
            <p><a href="mailto:pedidos@funcomunion.es" className="flex items-center gap-2 hover:text-white"><Mail className="w-4 h-4" aria-hidden="true" />pedidos@funcomunion.es</a></p>
          </div>
          {grupos.map(g => (
            <nav key={g.titulo} aria-label={g.titulo}>
              <p className="font-bold text-xs tracking-[0.15em] uppercase text-[#F4E4A6] mb-3">{g.titulo}</p>
              <ul className="space-y-2 list-none p-0 m-0">
                {g.items.map(l => (
                  <li key={l.slug}><a href={`${l.lang === 'en' ? '/en/' : '/'}${l.slug}`} className="text-sm text-white/75 hover:text-white">{l.nav}</a></li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap gap-4 text-xs text-white/70">
            {t.legal.map(([href, etiqueta]) => (
              <a key={href} href={href} className="hover:text-white">{etiqueta}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
